/**
 * ALL DOCUMENT ATOMS
 *
 * Central export for all Jotai atoms for Firebase collections
 * Each uses the generic createDocAtoms factory for consistency
 */

import { AllergyConstructor } from "#customTypes/composedTypes/allergyTypes"
import { ArtworkTagConstructor } from "#customTypes/composedTypes/artworkTagTypes"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import { EvalCommentConstructor } from "#customTypes/composedTypes/evalCommentTypes"
import {
  EvaluationConstructor,
  EvaluationTrait,
} from "#customTypes/composedTypes/evaluationTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { HelpRequestConstructor } from "#customTypes/composedTypes/helpRequestTypes"
import { MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import { NotificationConstructor } from "#customTypes/composedTypes/notificationTypes"
import {
  PortfolioCommentConstructor,
  StudentPortfolioConstructor,
} from "#customTypes/composedTypes/portfolioTypes"
import { ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { QuickNotes } from "#customTypes/composedTypes/quickNotesTypes"
import { SemesterConstructor } from "#customTypes/composedTypes/semesterTypes"
import {
  EvaluationKeywordConstructor,
  StaffNoteConstructor,
  StandoutMomentConstructor,
} from "#customTypes/composedTypes/standoutTypes"
import { StoryTimeConstructor } from "#customTypes/composedTypes/storyTimeTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { TagConstructor } from "#customTypes/composedTypes/tagTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { VersionConstructor } from "#customTypes/composedTypes/versionTypes"
import { type FamilyDeliveryConstructor } from "#utils/familyDelivery"
import { type SemesterCalendarDoc } from "#utils/semesterCalendar"

import { createDocAtoms } from "./createDocAtoms"

// Main collections
export const studentAtoms = createDocAtoms<StudentConstructor>(
  DOC_TYPES.STUDENTS
)
export const classAtoms = createDocAtoms<ClassConstructor>(DOC_TYPES.CLASSES)
export const classSessionAtoms = createDocAtoms<ClassSessionConstructor>(
  DOC_TYPES.CLASS_SESSIONS
)
export const curriculumAtoms = createDocAtoms<CurriculumConstructor>(
  DOC_TYPES.CURRICULUMS
)
export const projectAtoms = createDocAtoms<ProjectConstructor>(
  DOC_TYPES.PROJECTS
)
export const tryoutAtoms = createDocAtoms<TryoutConstructor>(DOC_TYPES.TRYOUTS)
export const makeupAtoms = createDocAtoms<MakeupConstructor>(DOC_TYPES.MAKEUPS)
export const tagAtoms = createDocAtoms<TagConstructor>(DOC_TYPES.TAGS)
export const allergyAtoms = createDocAtoms<AllergyConstructor>(
  DOC_TYPES.ALLERGIES
)
export const userAtoms = createDocAtoms<UserConstructor>(DOC_TYPES.USERS)

// Additional collections
export const evaluationAtoms = createDocAtoms<EvaluationConstructor>(
  DOC_TYPES.EVALUATIONS
)
export const evalTraitAtoms = createDocAtoms<EvaluationTrait>(
  DOC_TYPES.EVAL_TRAITS
)
export const semesterAtoms = createDocAtoms<SemesterConstructor>(
  DOC_TYPES.SEMESTERS
)
export const semesterCalendarAtoms = createDocAtoms<SemesterCalendarDoc>(
  DOC_TYPES.SEMESTERS
)
export const quickNotesAtoms = createDocAtoms<QuickNotes>(DOC_TYPES.QUICK_NOTES)
export const versionAtoms = createDocAtoms<VersionConstructor>(
  DOC_TYPES.VERSIONS
)
export const helpRequestAtoms = createDocAtoms<HelpRequestConstructor>(
  DOC_TYPES.HELP_REQUESTS
)

// Standout Moments
export const evaluationKeywordAtoms =
  createDocAtoms<EvaluationKeywordConstructor>(DOC_TYPES.EVALUATION_KEYWORDS)
export const standoutMomentAtoms = createDocAtoms<StandoutMomentConstructor>(
  DOC_TYPES.STANDOUT_MOMENTS
)
export const storyTimeAtoms = createDocAtoms<StoryTimeConstructor>(
  DOC_TYPES.STORY_TIMES
)
export const familyDeliveryAtoms = createDocAtoms<FamilyDeliveryConstructor>(
  DOC_TYPES.FAMILY_DELIVERIES
)
// Manager-only longer-term notes about staff members (see StaffNoteConstructor
// for why these are not on user docs).
export const staffNoteAtoms = createDocAtoms<StaffNoteConstructor>(
  DOC_TYPES.STAFF_NOTES
)

// Notifications
export const notificationAtoms = createDocAtoms<NotificationConstructor>(
  DOC_TYPES.NOTIFICATIONS
)
export const evalCommentAtoms = createDocAtoms<EvalCommentConstructor>(
  DOC_TYPES.EVAL_COMMENTS
)
export const portfolioCommentAtoms =
  createDocAtoms<PortfolioCommentConstructor>(DOC_TYPES.PORTFOLIO_COMMENTS)
export const portfolioAtoms = createDocAtoms<StudentPortfolioConstructor>(
  DOC_TYPES.PORTFOLIOS
)

// The shared artwork/portfolio tag dictionary. Subscribed inside
// useArtworkTags, never at app root — see JARVIS-158.
export const artworkTagAtoms = createDocAtoms<ArtworkTagConstructor>(
  DOC_TYPES.ARTWORK_TAGS
)
